import { LiveAnnouncer } from '@angular/cdk/a11y';
import { CdkListbox, CdkOption } from '@angular/cdk/listbox';
import { CdkConnectedOverlay, CdkOverlayOrigin } from '@angular/cdk/overlay';

import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  inject,
  Input,
  Output,
  QueryList,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, NgControl, ReactiveFormsModule } from '@angular/forms';
import { IconComponent } from '@shared/ui-shared';
import { Subject } from 'rxjs';
import { CslCdkListboxDirective } from '../../directives/csl-cdk-listbox/csl-cdk-listbox.directive';
import { InputPatternDirective } from '../../directives/input-pattern/input-pattern.directive';
import { InputDirective } from '../../directives/input/input.directive';
import { IconsPathProvider } from '../../providers/icons-path.provider';

const CDK_IMPORTS = [CdkConnectedOverlay, CdkOverlayOrigin];
const CSL_IMPORTS = [InputDirective, InputPatternDirective, IconComponent];

@Component({
  selector: 'csl-autocomplete',
  standalone: true,
  imports: [ReactiveFormsModule, ...CDK_IMPORTS, ...CSL_IMPORTS],
  templateUrl: './autocomplete.component.html',
  styleUrls: ['./autocomplete.component.css', '../../../styles/dropdown.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [{ provide: CdkListbox, useExisting: CslCdkListboxDirective }, IconsPathProvider],
  hostDirectives: [CslCdkListboxDirective],
  host: {
    class: 'block',
    '(focusin)': 'focus(); open()',
    '(focusout)': 'blur(); close()',
    '(mousedown)': 'isOpen ? close() : open()',
    '[attr.role]': 'null',
    '[attr.aria-disabled]': 'null',
    '[attr.aria-multiselectable]': 'null',
    '[attr.aria-orientation]': 'null',
    '[attr.aria-activedescendant]': 'null',
    '[attr.aria-owns]': 'isOpen ? listboxId : null',
  },
})
export class AutocompleteComponent<T> {
  private _listbox = inject(CslCdkListboxDirective) as CslCdkListboxDirective<T>;
  private _ngControl = inject(NgControl, { optional: true, self: true });
  private _cdr = inject(ChangeDetectorRef);
  private _liveAnnouncer = inject(LiveAnnouncer);

  private _isLoading = false;

  inputControl = new FormControl();
  isOpen = false;
  isFocused = false;
  isDisabled = false;

  public get options(): QueryList<CdkOption<T>> | undefined {
    return this._listbox.items;
  }

  @Input() id?: string;
  @Input() placeholder?: string;
  @Input() maxLength?: number;
  @Input() set isLoading(value: boolean) {
    this._isLoading = value;
    // queue a microtask to wait for the options to be updated before announcing suggestions
    queueMicrotask(() => this._announceSuggestions(value));
  }

  get isLoading(): boolean {
    return this._isLoading;
  }
  @Input() selectionRequired = false;
  @Input() inputPattern?: string | RegExp;
  @Input() displayFn?: (value: T | null) => string;
  @Input('aria-invalid') ariaInvalid?: string | null;
  @Input() ariaDescribedby?: string | null;
  @Input() ariaDescription?: string | null;
  @Input() ariaLabel?: string;
  @Input() ariaLabelledby?: string;

  @Output() focusChange = new EventEmitter();
  @Output() inputChange = new Subject<string>();

  get listboxId(): string | null {
    return this.id ? `${this.id}-listbox` : null;
  }

  get isListboxAvailable(): boolean {
    return this.isLoading || !!this.options?.length;
  }

  get activeDescendantId(): string | null {
    if (!this.isOpen || !this.isListboxAvailable) {
      return null;
    }

    return this._listbox.keyManager?.activeItem?.id ?? null;
  }

  private _onChange: (value: T | null) => void = () => {
    /* empty */
  };
  private _onTouched: () => void = () => {
    /* empty */
  };

  constructor() {
    if (this._ngControl) {
      // Note: we provide the value accessor through here, instead of
      // the `providers` to avoid running into a circular import.
      // CdkListbox already have own value accessor.
      this._ngControl.valueAccessor = this;
    }

    this._listbox.mode = 'autocomplete';
    this._setSubscriptions();
  }

  handleInput(event: KeyboardEvent): void {
    if (event.code === 'Space') {
      // Seems like Angular CDK Listbox have custom event handler, which prevent Space button keydown event at some reason.
      // So we catch the event here, for prevent it from to be prevented :)
      event.stopPropagation(); // fix bug with ignoring whitespace
    }
  }

  open() {
    if (this.isDisabled) return;
    this.isOpen = true;
  }

  close() {
    this.isOpen = false;
    this._listbox.keyManager?.setActiveItem(-1);
  }

  focus() {
    if (this.isDisabled) return;
    this.isFocused = true;
    this.focusChange.emit(this.isFocused);
  }

  blur() {
    this.isFocused = false;
    this.focusChange.emit(this.isFocused);
    if (!this.isOpen) {
      this._onTouched();
    }
  }

  onDropdownClose() {
    if (!this.isFocused) {
      this._onTouched();
    }
  }

  setInputValue(value: T | null) {
    const val = this.displayFn ? this.displayFn(value) : value ? String(value) : '';
    this.inputControl.setValue(val, { emitEvent: false });
  }

  // ControlValueAccessor
  writeValue(value: T | null): void {
    this.setInputValue(value);
  }

  registerOnChange(fn: (value: unknown) => void): void {
    this._onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this._onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.isDisabled = isDisabled;

    if (isDisabled) {
      this.inputControl.disable({ emitEvent: false });
    } else {
      this.inputControl.enable({ emitEvent: false });
    }
  }

  private _setSubscriptions() {
    this._listbox.valueChange.pipe(takeUntilDestroyed()).subscribe((v) => {
      const value = v.value[0]; // emit single value instead of provided array
      this._onChange(value);
      this.setInputValue(value);
      this._cdr.markForCheck();
      this.close();
    });

    this.inputControl.valueChanges.pipe(takeUntilDestroyed()).subscribe((v) => {
      this.inputChange.next(v);
      this.open();
      if (this.selectionRequired) {
        this._onChange(null);
      } else {
        this._onChange(v);
      }
    });
  }

  private _announceSuggestions(isLoading: boolean) {
    if (!this.isOpen) return;

    if (isLoading) {
      this._liveAnnouncer.announce('Loading suggestions.', 'polite');
    } else {
      const count = this.options?.length ?? 0;
      const message = count
        ? `${count} ${count === 1 ? 'suggestion is' : 'suggestions are'} available. Use the Down Arrow key to review them.`
        : 'No suggestions available.';
      this._liveAnnouncer.announce(message, 'polite');
    }
  }
}
